/**
 * RecordsApp Component
 * 
 * Displays unprocessed/incoming records from telegrambad table.
 * Provides filtering, pagination, and access to record details.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../../lib/rpc';
import RecordModal from './RecordModal';
import './Cases.css';

export default function RecordsApp() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [archived, setArchived] = useState(false); // по умолчанию активные
  const [withDupesOnly, setWithDupesOnly] = useState(false);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState(null);

  const query = useMemo(() => ({
    search: search.trim() || null,
    archived,
    withDupesOnly,
    limit,
    offset,
  }), [search, archived, withDupesOnly, limit, offset]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listRecords(query);
      setRows(data);
    } catch (e) {
      console.error('Error loading records:', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { 
    load(); 
  }, [load]); // перезагрузка при изменении фильтров

  function afterActionRefresh() {
    // обновить текущую страницу после действий из модалки
    load();
  }

  return (
    <div className="records-container">
      <div className="records-filters">
        <input
          placeholder="Поиск по content"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          className="records-filter-input"
        />
        <select
          value={archived === null ? 'all' : archived ? 'archived' : 'active'}
          onChange={(e) => {
            setOffset(0);
            setArchived(e.target.value === 'all' ? null : e.target.value === 'archived');
          }}
          className="records-filter-select"
        >
          <option value="active">Активные</option>
          <option value="archived">Архив</option>
          <option value="all">Все</option>
        </select>
        <label className="records-filter-checkbox">
          <input 
            type="checkbox" 
            checked={withDupesOnly} 
            onChange={(e) => { setWithDupesOnly(e.target.checked); setOffset(0); }} 
          />
          Только с дублями
        </label>
        <select 
          value={limit} 
          onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }} 
          className="records-filter-select"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Загрузка…</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Ничего не найдено</div>
      ) : (
        <table className="records-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Snippet</th>
              <th>Дубли</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td className="truncate" title={r.content ?? ''}>
                  {r.content}
                </td>
                <td>
                  {r.has_duplicates ? `есть (${r.duplicates_count})` : '—'}
                </td>
                <td>{r.archived ? 'архив' : 'активна'}</td>
                <td>
                  <button 
                    onClick={() => setOpenId(r.id)} 
                    className="btn btn-primary"
                  >
                    Открыть
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="records-pagination">
        <button 
          disabled={offset === 0} 
          onClick={() => setOffset(Math.max(0, offset - limit))} 
          className="btn btn-secondary"
        >
          Назад
        </button>
        <button 
          onClick={() => setOffset(offset + limit)} 
          className="btn btn-secondary"
        >
          Вперёд
        </button>
      </div>

      {openId !== null && (
        <RecordModal
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={afterActionRefresh}
        />
      )}
    </div>
  );
}
